import { useNavigate } from 'react-router-dom'
import { AxiosError } from 'axios'

import { StatusCodes } from '@e3dc-react/shell/Libs/http-status-codes'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { E3dcFetchError } from '@e3dc-react/shell/Types/api'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'
import { AppLang } from '@e3dc-react/shell/State/App/AppModel'
import { UserLevel, UserModel } from 'src/State/User/UserModel'
import { ApiResult, usePortalMutation } from 'src/Libs/api'
import { handleFetchError } from 'src/Libs/api/utils'
import { ContactData } from 'src/Libs/user/models'
import {
  updateUserCompany,
  updateUser as updateUserRequest,
  inviteUser,
  deleteUser,
  resendIpinEmail,
  resendInvitationEmail,
  changeUserLanguage,
} from 'src/Libs/user/api/user.api'
import { UserForm } from '../types/default'
import { useUserPermissionsMutate, UserPermissionTypes } from 'src/Libs/company-settings'
import { INSTALLER_PARTNER } from 'src/Configuration'
import { fetchCompany } from '../api/company.api'

interface UseUserArgs {
  createdBy?: number
  onSuccess?: () => void
}

interface UpdateUserParams {
  userId: number
  locationId: number | null
  initialCompanyId?: number | null
  formValues: Partial<UserForm>
  setFieldError?: (fieldName: keyof UserForm, errorMessage: string) => void
  companyIdForPermissions?: number
  currentCompanyHasShop?: boolean
  sendEmail?: boolean
  initialUseShop?: boolean
}

interface UseUserReturningType {
  createUser: (formValues: UserForm, sendEmail?: boolean) => Promise<void>
  updateUser: (params: UpdateUserParams) => Promise<void>
  changePermissions: (formValues: Partial<UserForm>, userId: number) => Promise<void>
  deleteUser: (userId: number) => void
  resendIpinEmail: (userId: number) => void
  resendInvitationEmail: (userId: number, sendIpin?: boolean) => void
}

const errorMessages = {
  serviceUserIsMissing: 'service user data is missing',
  userExists: 'user already exists',
}

interface CreateUserMutationVariables {
  createdBy: number
  formValues: UserForm
  sendIpin?: boolean
}

interface UpdateUserCompanyMutationVariables {
  userId: number
  payload: {
    companyID: number
  }
}

interface UserLocationMutationVariables {
  userId: number
  locationId: number | null
  payload: Partial<ContactData>
}

interface UpdateUserMutationVariables {
  userId: number
  payload: Partial<UserModel>
}

interface UpdateUserLanguageMutationVariables {
  userId: number
  language: AppLang
}

const isSuccessfulWriteStatus = (status: number): boolean => status >= StatusCodes.OK && status < 300

/**
 * Hook that return update function for user data
 * @param createdBy - indicates who invited new user
 */
export const useUser = (args: UseUserArgs | undefined): UseUserReturningType => {
  const createdBy = args?.createdBy
  const onSuccess = args?.onSuccess
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const navigate = useNavigate()
  const [enablePermissions, disablePermissions] = useUserPermissionsMutate({})
  const createUserMutation = usePortalMutation<number, unknown, CreateUserMutationVariables>({
    mutationFn: async ({ createdBy: invitedBy, formValues, sendIpin }) => {
      try {
        const createdUserId = await inviteUser(formValues, authFetch, invitedBy, sendIpin)
        return new ApiResult<number>(createdUserId)
      } catch (error) {
        return handleFetchError<number>(error)
      }
    },
  })
  const updateUserCompanyMutation = usePortalMutation<void, unknown, UpdateUserCompanyMutationVariables>({
    mutationFn: ({ payload, userId }) => updateUserCompany(userId, payload, authFetch),
  })
  const updateUserLocationMutation = usePortalMutation<void, unknown, UserLocationMutationVariables>({
    mutationFn: async ({ locationId, payload, userId }) => {
      try {
        const response = await authFetch<void>({
          method: locationId ? 'PUT' : 'POST',
          path: `users/${userId}/location`,
          payload,
        })

        if (isSuccessfulWriteStatus(response.status)) {
          return new ApiResult<void>(null, false)
        }

        return new ApiResult<void>(null, true)
      } catch (error) {
        return handleFetchError<void>(error)
      }
    },
  })
  const updateUserDataMutation = usePortalMutation<void, unknown, UpdateUserMutationVariables>({
    mutationFn: ({ payload, userId }) => updateUserRequest(userId, payload, authFetch),
  })
  const updateLanguageMutation = usePortalMutation<void, unknown, UpdateUserLanguageMutationVariables>({
    mutationFn: ({ language, userId }) => changeUserLanguage(userId, language, authFetch),
  })
  const resendIpinEmailMutation = usePortalMutation<void, unknown, number>({
    mutationFn: userId => resendIpinEmail(userId, authFetch),
  })
  const resendInvitationEmailMutation = usePortalMutation<void, unknown, { userId: number; sendIpin: boolean }>({
    mutationFn: ({ userId, sendIpin }) => resendInvitationEmail(userId, sendIpin, authFetch, createdBy),
  })

  const changePermissions = async (formValues: Partial<UserForm>, userId: number): Promise<void> => {
    const { useShop, company } = formValues
    const userLevel = formValues.userLevel as UserLevel

    // shop permission can be activated/diactivated for installer/partner users only
    if (INSTALLER_PARTNER.includes(userLevel)) {
      if (!company) {
        const errorMessage = 'Change permissions data error - there is no company'
        console.error(errorMessage)
        throw new Error(errorMessage)
      }

      await (useShop ? enablePermissions : disablePermissions).mutateAsync({
        companyId: company,
        userId,
        permissionType: UserPermissionTypes.USE_SHOP,
      })
    }
  }

  const createUser = async (formValues: UserForm, sendIpin?: boolean): Promise<void> => {
    try {
      if (!createdBy) {
        throw new Error(errorMessages.serviceUserIsMissing)
      }

      const createdUserId = await createUserMutation.mutateAsync({ createdBy, formValues, sendIpin })

      if (createdUserId === null) {
        throw new Error(errorMessages.serviceUserIsMissing)
      }

      if (formValues.useShop) {
        await changePermissions(formValues, createdUserId)
      }

      enqueueSuccessStack(t('user.save.success'))
      navigate('/overview/users', { replace: true })
      onSuccess?.()
    } catch (error) {
      const errorData = error as AxiosError<E3dcFetchError>

      console.error(errorData)
      if (errorData?.response?.data.errno === FetchErrorCodes.UserAlreadyExists) {
        enqueueErrorStack(t('user.save.username-unique'))
      } else {
        enqueueErrorStack(t('user.save.error', { error: errorData?.response?.data.message }))
      }
    }
  }

  const updateUser = async ({
    companyIdForPermissions,
    currentCompanyHasShop,
    formValues,
    setFieldError,
    locationId,
    userId,
    initialCompanyId,
    initialUseShop,
  }: UpdateUserParams): Promise<void> => {
    const {
      username,
      firstName,
      lastName,
      userLevel,
      eMailNotification,
      eMail,
      country,
      address,
      city,
      zipCode,
      phoneNumber,
      ipin,
      infinityCertified,
      language,
      couponPermission,
      qntmCertified,
    } = formValues

    const userDataToUpdate: Partial<UserModel> = {
      ...(username && { username }),
      Name: lastName,
      Vorname: firstName,
      level: userLevel,
      mailNotification: eMailNotification,
      ipin,
      infinityCertified: infinityCertified ?? false,
      couponPermission,
      qntmCertified,
    }

    /** in the backend, street => PlaceOr, Ort => Address */
    const userLocation: Partial<ContactData> = {
      Address: city,
      PLZ: zipCode,
      PlaceOR: address,
      Land: country,
      Telefon: phoneNumber,
      Email: eMail,
    }

    try {
      const companyID = formValues.company

      /** only send request if company id is set and different then initial **/
      if (companyID && !Number.isNaN(companyID) && initialCompanyId !== companyID) {
        await updateUserCompanyMutation.mutateAsync({ userId, payload: { companyID } })
      }

      const updateUserLocation = updateUserLocationMutation.mutateAsync({
        locationId,
        payload: userLocation,
        userId,
      })
      const updateUserData = updateUserDataMutation.mutateAsync({ payload: userDataToUpdate, userId })
      const updateLanguage = updateLanguageMutation.mutateAsync({ language: language as AppLang, userId })

      await Promise.all([updateUserLocation, updateUserData, updateLanguage])

      await updateShopPermissionIfNecessary({ formValues, userId, currentCompanyHasShop, initialUseShop, companyIdForPermissions, initialCompanyId })

      enqueueSuccessStack(t('user.save.success'))
      onSuccess?.()
    } catch (error) {
      const errorData = error as AxiosError<E3dcFetchError>

      /* notify user about BE validation. */
      if (errorData.response?.data.error.includes(errorMessages.userExists)) {
        setFieldError?.('username', t('user.save.username-unique'))
      } else {
        enqueueErrorStack(t('user.save.error', { error: errorData.message }))
      }
    }
  }

  const updateShopPermissionIfNecessary = async (props: {
    initialUseShop?: boolean
    currentCompanyHasShop?: boolean
    initialCompanyId?: number | null
    companyIdForPermissions?: number
    formValues: Partial<UserForm>
    userId: number
  }): Promise<void> => {
    const { formValues, initialUseShop, currentCompanyHasShop, companyIdForPermissions, initialCompanyId, userId } = props

    if (formValues.useShop !== initialUseShop && companyIdForPermissions) {
      let companyHasShop = currentCompanyHasShop ?? false

      if (initialCompanyId !== companyIdForPermissions) {
        const selectedCompanyResult = await fetchCompany(companyIdForPermissions, authFetch)

        if (selectedCompanyResult.hasErrors || !selectedCompanyResult.value) {
          throw selectedCompanyResult.error ?? new Error('Change permissions data error - there is no selected company')
        }

        companyHasShop = selectedCompanyResult.value.shopPermission
      }

      if (companyHasShop) {
        await changePermissions(
          {
            ...formValues,
            company: companyIdForPermissions,
          },
          userId
        )
      }
    }
  }

  const handleDeleteUser = (userId: number): void => {
    deleteUser(userId, authFetch)
      .then(() => {
        enqueueSuccessStack(t('administration.userRemoveSuccess'))
        navigate('/overview/users', { replace: true })
      })
      .catch(() => {
        enqueueErrorStack(t('administration.userRemoveError'))
      })
  }

  const handleResendIpinEmail = (userId: number): void => {
    resendIpinEmailMutation.mutate(userId, {
      onSuccess: () => {
        enqueueSuccessStack(t('user.installer.resend-ipin.success'))
      },
      onError: () => {
        enqueueErrorStack(t('user.installer.resend-ipin.error'))
      },
    })
  }

  const handleResendInvitation = (userId: number, sendIpin = true): void => {
    resendInvitationEmailMutation.mutate(
      { userId, sendIpin },
      {
        onSuccess: () => {
          enqueueSuccessStack(t('user.installer.resend-invitation.success'))
        },
        onError: () => {
          enqueueErrorStack(t('user.installer.resend-invitation.error'))
        },
      }
    )
  }

  return {
    createUser,
    updateUser,
    changePermissions,
    deleteUser: handleDeleteUser,
    resendIpinEmail: handleResendIpinEmail,
    resendInvitationEmail: handleResendInvitation,
  }
}
